import type {
    RadarChartPackedConstantType,
    RadarChartLayoutConstantType,
    RadarChartIndexConstantType,
    RadarChartPointConstantType,
    RadarChartLayout,
    RadarChartShape,
    RadarChartLabel
} from "./type";
import {
    RADAR_CHART_LABEL_BASELINE_SHIFT
} from "./stylesheet";
import type {
    ChartPoint
} from "../chart/type";
import {
    getChartTextWidth
} from "../chart/util";

export const RADAR_CHART_LABEL_ANCHOR_THRESHOLD = 0.2;

export const RADAR_CHART_SHAPE_HEADER = 3;

export const getRadarChartAngles = (count: number, isRTL: boolean): Array<number> => {
    const direction = isRTL ? -1 : 1;

    return Array.from({
        length: count
    }, (_, index) => -Math.PI / 2 + direction * 2 * Math.PI * index / Math.max(1, count));
};

export const getRadarChartPoint = ({
    layout,
    radius,
    index
}: RadarChartPointConstantType): ChartPoint => {
    const angle = layout.angles[index] ?? 0;

    return {
        x: layout.cx + radius * Math.cos(angle),
        y: layout.cy + radius * Math.sin(angle)
    };
};

export const getRadarChartLayout = ({
    measures,
    height,
    labels,
    isRTL,
    width
}: RadarChartLayoutConstantType): RadarChartLayout => {
    const angles = getRadarChartAngles(labels.length, isRTL);

    const fontSize = measures.typography.fontSize;

    const gap = measures.spacingSm;

    const cx = width / 2;

    const cy = height / 2;

    const limits = angles.map((angle, index) => {
        const cos = Math.cos(angle);

        const sin = Math.sin(angle);

        const labelWidth = getChartTextWidth({
            text: labels[index] ?? "",
            fontSize
        });

        const extent = Math.abs(cos) < RADAR_CHART_LABEL_ANCHOR_THRESHOLD ? labelWidth / 2 : labelWidth;

        const horizontal = Math.abs(cos) < 1e-6 ? Infinity : (cx - extent) / Math.abs(cos);

        const vertical = Math.abs(sin) < 1e-6 ? Infinity : (cy - fontSize) / Math.abs(sin);

        return Math.min(horizontal, vertical) - gap;
    });

    const radius = Math.max(0, Math.min(cx, cy, ...limits));

    const placed = angles.map((angle, index): RadarChartLabel => {
        const cos = Math.cos(angle);

        return {
            anchor: Math.abs(cos) < RADAR_CHART_LABEL_ANCHOR_THRESHOLD ? "middle" : cos > 0 ? "start" : "end",
            y: cy + (radius + gap) * Math.sin(angle) + fontSize * RADAR_CHART_LABEL_BASELINE_SHIFT,
            x: cx + (radius + gap) * cos,
            text: labels[index] ?? ""
        };
    });

    return {
        labels: placed,
        angles,
        radius,
        cx,
        cy
    };
};

export const getRadarChartIndex = ({
    layout,
    count,
    isRTL,
    point
}: RadarChartIndexConstantType): number | undefined => {
    if(!count) {
        return undefined;
    }

    const step = 2 * Math.PI / count;

    const turn = Math.atan2(point.y - layout.cy, point.x - layout.cx) + Math.PI / 2;

    const index = Math.round((isRTL ? -turn : turn) / step);

    return ((index % count) + count) % count;
};

export const getRadarChartPolygonPath = (points: Array<ChartPoint>): string => {
    if(!points.length) {
        return "";
    }

    return `M${points.map((point) => `${Math.round(point.x * 100) / 100} ${Math.round(point.y * 100) / 100}`).join(" L")} Z`;
};

export const getRadarChartPacked = ({
    values,
    domain
}: RadarChartPackedConstantType): Array<number> => {
    return [
        domain,
        values.length,
        values[0]?.length ?? 0,
        ...values.flat()
    ];
};

export const getRadarChartUnpacked = (packed: Array<number>): {
    values: Array<Array<number>>;
    shape: RadarChartShape;
} => {
    const seriesCount = Math.round(packed[1] ?? 0);

    const axisCount = Math.round(packed[2] ?? 0);

    return {
        values: Array.from({
            length: seriesCount
        }, (_, series) => packed.slice(RADAR_CHART_SHAPE_HEADER + series * axisCount, RADAR_CHART_SHAPE_HEADER + (series + 1) * axisCount)),
        shape: {
            domain: packed[0] || 1,
            seriesCount,
            axisCount
        }
    };
};
